/**
 * TemplatePropertyMapper Component
 * 
 * Manages property mappings within a template.
 * Features drag-and-drop ordering and required toggles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Switch,
  Tag,
  Typography,
  message,
  Modal,
  Empty,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { PropertyLevelService } from '../../services/api/propertyLevelService';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { 
  GlobalPropertyTemplate, 
  TemplatePropertyMapping,
  PropertyType 
} from '../../types/propertyLevel';
import { PropertyTypeIcon } from '../Shared/PropertyTypeIcon';
import styles from './TemplatePropertyMapper.module.css';

const { Text } = Typography;

interface TemplatePropertyMapperProps {
  template: GlobalPropertyTemplate;
  onUpdate?: () => void;
}

interface GlobalPropertyOption {
  property_key: string;
  property_type: string;
  description?: string;
}

export const TemplatePropertyMapper: React.FC<TemplatePropertyMapperProps> = ({
  template,
  onUpdate
}) => {
  const [properties, setProperties] = useState<TemplatePropertyMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [globalProperties, setGlobalProperties] = useState<GlobalPropertyOption[]>([]);
  const [loadingGlobalProperties, setLoadingGlobalProperties] = useState(false);
  const [selectedPropertyKeys, setSelectedPropertyKeys] = useState<string[]>([]);

  // Load properties from template
  useEffect(() => {
    console.log(`[TemplatePropertyMapper] ===== Template prop changed START =====`);
    console.log(`[TemplatePropertyMapper] Template:`, {
      id: template.id,
      template_key: template.template_key,
      template_name: template.template_name,
      propertiesCount: template.properties?.length || 0,
      properties: template.properties?.map(p => ({
        id: p.id,
        property_key: p.property_key,
        property_type: p.property_type,
        is_required: p.is_required,
        display_order: p.display_order
      })) || [],
      rawProperties: template.properties
    });
    
    if (template.properties && template.properties.length > 0) {
      // Filter out any null properties (from deleted mappings)
      const validProperties = template.properties.filter(p => p !== null && p !== undefined);
      const sortedProperties = [...validProperties].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      );
      console.log(`[TemplatePropertyMapper] Setting ${sortedProperties.length} properties from template prop:`, sortedProperties.map(p => p.property_key));
      setProperties(sortedProperties);
    } else {
      console.log(`[TemplatePropertyMapper] No properties in template (properties=${template.properties}), clearing local state`);
      setProperties([]);
    }
    console.log(`[TemplatePropertyMapper] ===== Template prop changed END =====`);
  }, [template]);

  // Fetch global properties for add modal
  const fetchGlobalProperties = useCallback(async () => {
    setLoadingGlobalProperties(true);
    try {
      const response = await GlobalPropertiesService.listPropertyDefinitions({
        skip: 0,
        limit: 1000,
        summary: true
      });
      
      const existingKeys = new Set(properties.map(p => p.property_key));
      const propertiesList = response.properties || [];
      const available: GlobalPropertyOption[] = [];
      
      for (const p of propertiesList) {
        const propKey = (p as any).property_key;
        if (propKey && !existingKeys.has(propKey)) {
          available.push({
            property_key: propKey,
            property_type: (p as any).property_type || 'string',
            description: (p as any).description
          });
        }
      }
      
      setGlobalProperties(available);
    } catch (error) {
      console.error('Error fetching global properties:', error);
      setGlobalProperties([]);
    } finally {
      setLoadingGlobalProperties(false);
    }
  }, [properties]);

  useEffect(() => {
    if (showAddModal) {
      void fetchGlobalProperties();
    }
  }, [showAddModal, fetchGlobalProperties]);

  // Handle add properties
  const handleAddProperties = async () => {
    if (selectedPropertyKeys.length === 0) {
      void message.warning('Please select at least one property');
      return;
    }
    
    setLoading(true);
    try {
      // Filter out properties that already exist in the template
      const existingPropertyKeys = new Set(properties.map(p => p.property_key));
      const propertiesToAdd = selectedPropertyKeys.filter(key => !existingPropertyKeys.has(key));
      
      if (propertiesToAdd.length === 0) {
        void message.warning('All selected properties already exist in this template');
        setLoading(false);
        return;
      }
      
      if (propertiesToAdd.length < selectedPropertyKeys.length) {
        void message.info(`${selectedPropertyKeys.length - propertiesToAdd.length} property(s) already exist and were skipped`);
      }
      
      // Use template.id (UUID) instead of template.template_key
      for (let i = 0; i < propertiesToAdd.length; i++) {
        const propertyKey = propertiesToAdd[i];
        
        // Find the property in globalProperties to get its type
        const globalProperty = globalProperties.find(p => p.property_key === propertyKey);
        
        if (!globalProperty) {
          console.warn(`Property type not found for ${propertyKey}, defaulting to 'string'`);
        }
        
        const propertyType = globalProperty?.property_type || 'string';
        
        await PropertyLevelService.addPropertyToTemplate(template.id, {
          property_key: propertyKey,
          property_type: propertyType,  // Required - Backend expects this field
          is_required: false,
          display_order: properties.length + i + 1
        });
      }
      
      console.log(`[TemplatePropertyMapper] Successfully added ${propertiesToAdd.length} properties to template ${template.id}`);
      void message.success(`Added ${propertiesToAdd.length} properties`);
      setShowAddModal(false);
      setSelectedPropertyKeys([]);
      
      // Call onUpdate to trigger parent to refresh template data
      if (onUpdate) {
        console.log('[TemplatePropertyMapper] Calling onUpdate to refresh template data');
        await onUpdate();
      }
      
      // Also try to refresh the template properties directly if we have the template
      // This ensures we have the latest data even if parent hasn't refreshed yet
      if (template.id) {
        try {
          console.log(`[TemplatePropertyMapper] Fetching updated template ${template.id} to refresh properties`);
          const updatedTemplate = await PropertyLevelService.getTemplate(template.id);
          console.log(`[TemplatePropertyMapper] Updated template received:`, {
            id: updatedTemplate.id,
            template_key: updatedTemplate.template_key,
            propertiesCount: updatedTemplate.properties?.length || 0,
            properties: updatedTemplate.properties?.map(p => p.property_key) || []
          });
          
          // Update local state if template structure matches
          if (updatedTemplate.properties) {
            const sortedProperties = [...updatedTemplate.properties].sort(
              (a, b) => a.display_order - b.display_order
            );
            setProperties(sortedProperties);
            console.log(`[TemplatePropertyMapper] Updated local properties state:`, sortedProperties.map(p => p.property_key));
          }
        } catch (error) {
          console.error('[TemplatePropertyMapper] Error refreshing template after adding properties:', error);
        }
      }
    } catch (error: any) {
      console.error('Error adding properties:', error);
      void message.error(error.response?.data?.detail || 'Failed to add properties');
    } finally {
      setLoading(false);
    }
  };

  // Handle remove property
  const handleRemoveProperty = async (mapping: TemplatePropertyMapping) => {
    if (!mapping.id) {
      console.error('[TemplatePropertyMapper] Cannot remove property: mapping.id is undefined', mapping);
      void message.error('Cannot remove property: missing mapping ID');
      return;
    }
    
    Modal.confirm({
      title: 'Remove Property',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to remove "${mapping.property_key}" from this template?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log(`[TemplatePropertyMapper] Removing property mapping:`, {
            templateId: template.id,
            mappingId: mapping.id,
            propertyKey: mapping.property_key
          });
          
          // Use template.id (UUID) and mapping.id (UUID) for delete
          await PropertyLevelService.removePropertyFromTemplate(
            template.id,
            mapping.id
          );
          void message.success('Property removed');
          if (onUpdate) {
            onUpdate();
          }
        } catch (error: any) {
          console.error('[TemplatePropertyMapper] Error removing property:', error);
          void message.error(error.response?.data?.detail || 'Failed to remove property');
        }
      }
    });
  };

  // Handle required toggle
  const handleRequiredToggle = async (mapping: TemplatePropertyMapping, isRequired: boolean) => {
    try {
      // Update locally first for immediate feedback
      setProperties(prev => 
        prev.map(p => 
          p.property_key === mapping.property_key 
            ? { ...p, is_required: isRequired } 
            : p
        )
      );
      
      // Note: API call would go here if backend supports updating individual mappings
      // For now, we just update locally
      void message.success(`Property ${isRequired ? 'marked as' : 'unmarked as'} required`);
    } catch (error: any) {
      void message.error('Failed to update property');
    }
  };

  // Handle move up/down
  const handleMove = async (propertyKey: string, direction: 'up' | 'down') => {
    const index = properties.findIndex(p => p.property_key === propertyKey);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= properties.length) return;

    const newProperties = [...properties];
    const temp = newProperties[index];
    newProperties[index] = newProperties[newIndex];
    newProperties[newIndex] = temp;

    // Update display_order
    newProperties.forEach((p, i) => {
      p.display_order = i + 1;
    });

    setProperties(newProperties);

    try {
      // Use template.id (UUID) instead of template.template_key
      await PropertyLevelService.updateTemplatePropertyOrder(
        template.id,
        newProperties.map(p => ({
          property_key: p.property_key,
          display_order: p.display_order
        }))
      );
    } catch (error) {
      console.error('Error updating order:', error);
      // Revert on error
      if (onUpdate) {
        onUpdate();
      }
    }
  };

  // Table columns
  const columns: ColumnsType<TemplatePropertyMapping> = [
    {
      title: '#',
      key: 'order',
      width: 60,
      render: (_, record, index) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">{index + 1}</Text>
          <Space size={0}>
            <Tooltip title="Move up">
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => handleMove(record.property_key, 'up')}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === properties.length - 1}
                onClick={() => handleMove(record.property_key, 'down')}
              />
            </Tooltip>
          </Space>
        </Space>
      )
    },
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      render: (text: string, record: TemplatePropertyMapping) => (
        <Space>
          <strong>{text}</strong>
          {record.global_property_ref && (
            <Tag color="green" style={{ fontSize: 10 }}>Global</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      width: 120,
      render: (type: PropertyType) => (
        <PropertyTypeIcon type={type} showLabel />
      )
    },
    {
      title: 'Required',
      key: 'is_required',
      width: 100,
      align: 'center',
      render: (_, record: TemplatePropertyMapping) => (
        <Switch
          size="small"
          checked={record.is_required}
          onChange={(checked) => handleRequiredToggle(record, checked)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record: TemplatePropertyMapping) => (
        <Tooltip title="Remove from template">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveProperty(record)}
          />
        </Tooltip>
      )
    }
  ];

  // Add modal columns
  const addModalColumns: ColumnsType<GlobalPropertyOption> = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key'
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      width: 120,
      render: (type: string) => (
        <PropertyTypeIcon type={type as PropertyType} showLabel />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary" ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Text>
      )
    }
  ];

  return (
    <div className={styles.mapper}>
      <div className={styles.header}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setShowAddModal(true)}
        >
          Add Property
        </Button>
        <Text type="secondary">
          Drag to reorder • Toggle required status • Remove properties
        </Text>
      </div>

      {properties.length === 0 ? (
        <Empty
          description="No properties in this template"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => setShowAddModal(true)}>
            Add First Property
          </Button>
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={properties}
          rowKey={(record) => record.id || `${record.property_key}-${record.display_order}`}
          pagination={false}
          size="small"
          loading={loading}
        />
      )}

      {/* Add Property Modal */}
      <Modal
        title="Add Properties to Template"
        open={showAddModal}
        onOk={handleAddProperties}
        onCancel={() => {
          setShowAddModal(false);
          setSelectedPropertyKeys([]);
        }}
        okText={`Add ${selectedPropertyKeys.length} Properties`}
        okButtonProps={{ disabled: selectedPropertyKeys.length === 0 }}
        width={700}
        confirmLoading={loading}
      >
        <Table
          columns={addModalColumns}
          dataSource={globalProperties}
          rowKey="property_key"
          loading={loadingGlobalProperties}
          size="small"
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedPropertyKeys,
            onChange: (keys) => setSelectedPropertyKeys(keys as string[])
          }}
          scroll={{ y: 300 }}
        />
      </Modal>
    </div>
  );
};

export default TemplatePropertyMapper;
